import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { SPYEE_TOKEN, SPYEE_REFRESH_TOKEN } from 'constants/index';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './storage';
import { EXPIRED_TOKEN_CODE, SESSION_EXPIRED_MESSAGE } from './auth-errors';

type PendingRequest = { retry: () => void; abort: (err: Error) => void };

let isRefreshing = false;
let pendingRequests: PendingRequest[] = [];

function resolvePendingRequests(): void {
    pendingRequests.forEach(({ retry }) => retry());
    pendingRequests = [];
}

// Reject queued requests so their observer errors out instead of hanging forever.
// Without this, consumers (e.g. useQuery hooks) would stay in loading state when
// the refresh fails between an in-flight retry and the redirect.
function rejectPendingRequests(reason: Error): void {
    pendingRequests.forEach(({ abort }) => abort(reason));
    pendingRequests = [];
}

function isSpyeeMode(): boolean {
    return !!window.TOTEM?.storage?.get?.(SPYEE_TOKEN);
}

type Tokens = { accessToken: string; refreshToken: string };

// Serialize refresh across tabs/windows of this origin via Web Locks API.
// Without this, two tabs waking from sleep both POST /auth/refresh with the same
// refresh token — one rotates successfully, the other gets 401 and logs the user out.
async function fetchNewTokens(apiUrl: string): Promise<Tokens | null> {
    const spyeeMode = isSpyeeMode();
    const readRefresh = (): string | null =>
        (spyeeMode ? window.TOTEM.storage.get(SPYEE_REFRESH_TOKEN) : getRefreshToken()) ?? null;
    const readAccess = (): string | null =>
        (spyeeMode ? window.TOTEM.storage.get(SPYEE_TOKEN) : getAccessToken()) ?? null;

    const initialRefreshToken = readRefresh();
    if (!initialRefreshToken) return null;

    const runRefresh = async (): Promise<Tokens | null> => {
        // Re-read after acquiring the lock: another tab may have rotated while we waited.
        const currentRefreshToken = readRefresh();
        if (!currentRefreshToken) return null;
        if (currentRefreshToken !== initialRefreshToken) {
            const accessToken = readAccess();
            if (!accessToken) return null;
            return { accessToken, refreshToken: currentRefreshToken };
        }

        const response = await fetch(`${apiUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: currentRefreshToken }),
        });

        if (!response.ok) {
            // Safety net: if storage rotated between request send and response,
            // another tab succeeded — use its tokens instead of forcing logout.
            const storedRefresh = readRefresh();
            const storedAccess = readAccess();
            if (storedRefresh && storedRefresh !== currentRefreshToken && storedAccess) {
                return { accessToken: storedAccess, refreshToken: storedRefresh };
            }
            return null;
        }

        const data = await response.json();
        return {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
        };
    };

    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return navigator.locks.request('totem-token-refresh', runRefresh);
    }
    return runRefresh();
}

function hasExpiredTokenError(result: FetchResult): boolean {
    return !!result.errors?.some((error) => (error.extensions?.code as string) === EXPIRED_TOKEN_CODE);
}

// Individual expired-token errors are not reported (they are expected, and recovered right here),
// so this forced logout is the auth event worth an alert: it is the one the user actually feels.
// Mirrors the totmobile client, which reports only this transition.
function redirectToLogin(reason: 'no_new_tokens' | 'refresh_threw', cause?: unknown): void {
    Sentry.captureMessage('Token refresh link: session expired, triggering logout', {
        level: 'warning',
        tags: { reason },
        extra: { cause: cause instanceof Error ? cause.message : cause },
    });
    clearTokens();
    window.location.href = '/login';
}

export function createTokenRefreshLink(apiUrl: string, onTokenRefreshed?: () => void): ApolloLink {
    return new ApolloLink((operation, forward) => {
        return new Observable<FetchResult>((observer) => {
            let isHandlingRefresh = false;

            forward(operation).subscribe({
                next: (result) => {
                    if (!hasExpiredTokenError(result)) {
                        observer.next(result);
                        return;
                    }

                    isHandlingRefresh = true;

                    if (isRefreshing) {
                        pendingRequests.push({
                            retry: () => {
                                const token = isSpyeeMode()
                                    ? window.TOTEM.storage.get(SPYEE_TOKEN) || ''
                                    : getAccessToken() || '';
                                const headerKey = isSpyeeMode() ? 'spiedusertoken' : 'authorization';
                                operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                                    headers: { ...headers, [headerKey]: token },
                                }));
                                forward(operation).subscribe(observer);
                            },
                            abort: (err) => observer.error(err),
                        });
                        return;
                    }

                    isRefreshing = true;

                    fetchNewTokens(apiUrl)
                        .then((tokens) => {
                            if (!tokens) {
                                rejectPendingRequests(new Error(SESSION_EXPIRED_MESSAGE));
                                redirectToLogin('no_new_tokens');
                                return;
                            }

                            if (isSpyeeMode()) {
                                window.TOTEM.storage.set(SPYEE_TOKEN, tokens.accessToken);
                                window.TOTEM.storage.set(SPYEE_REFRESH_TOKEN, tokens.refreshToken);
                            } else {
                                setTokens(tokens.accessToken, tokens.refreshToken);
                            }
                            // Force WebSocket reconnection with the fresh token
                            onTokenRefreshed?.();

                            const headerKey = isSpyeeMode() ? 'spiedusertoken' : 'authorization';
                            operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                                headers: { ...headers, [headerKey]: tokens.accessToken },
                            }));

                            resolvePendingRequests();
                            forward(operation).subscribe(observer);
                        })
                        .catch((err) => {
                            rejectPendingRequests(err instanceof Error ? err : new Error('Token refresh failed'));
                            redirectToLogin('refresh_threw', err);
                        })
                        .finally(() => {
                            isRefreshing = false;
                        });
                },
                error: (error) => observer.error(error),
                complete: () => {
                    if (!isHandlingRefresh) {
                        observer.complete();
                    }
                },
            });
        });
    });
}
